(() => {
  const SELECTOR = '[data-bh-hero-showcase]';

  class BHHeroShowcase {
    constructor(root) {
      this.root = root;
      /* Compatibilidad: elimina la franja si quedó impresa por un Liquid anterior. */
      root.querySelectorAll('.bh-hero-showcase__promo').forEach((promo) => promo.remove());
      this.slides = [...root.querySelectorAll('[data-bh-showcase-slide]')];
      this.dots = [...root.querySelectorAll('[data-bh-showcase-dot]')];
      this.viewport = root.querySelector('[data-bh-showcase-viewport]');
      this.previous = root.querySelector('[data-bh-showcase-prev]');
      this.next = root.querySelector('[data-bh-showcase-next]');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.interval = Math.max(3000, Number(root.dataset.interval) || 6000);
      this.autoplay = root.dataset.autoplay === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.timer = 0;
      this.touchX = 0;
      this.touchY = 0;
      this.visible = true;
      this.controller = new AbortController();
      this.signal = this.controller.signal;

      this.bind();
      this.show(this.index, false);
      this.observe();
      this.start();
    }

    bind() {
      this.previous?.addEventListener('click', () => this.step(-1, true), { signal: this.signal });
      this.next?.addEventListener('click', () => this.step(1, true), { signal: this.signal });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.show(Number(dot.dataset.bhShowcaseDot), true);
        }, { signal: this.signal });
      });

      this.root.addEventListener('mouseenter', () => this.stop(), { signal: this.signal });
      this.root.addEventListener('mouseleave', () => this.start(), { signal: this.signal });
      this.root.addEventListener('focusin', () => this.stop(), { signal: this.signal });
      this.root.addEventListener('focusout', (event) => {
        if (!this.root.contains(event.relatedTarget)) this.start();
      }, { signal: this.signal });

      this.viewport?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.step(-1, true);
        if (event.key === 'ArrowRight') this.step(1, true);
      }, { signal: this.signal });

      this.viewport?.addEventListener('touchstart', (event) => {
        const touch = event.changedTouches[0];
        this.touchX = touch.clientX;
        this.touchY = touch.clientY;
        this.stop();
      }, { passive: true, signal: this.signal });

      this.viewport?.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        const deltaX = this.touchX - touch.clientX;
        const deltaY = this.touchY - touch.clientY;
        if (Math.abs(deltaX) >= 42 && Math.abs(deltaX) > Math.abs(deltaY)) {
          this.step(deltaX > 0 ? 1 : -1, true);
        } else {
          this.start();
        }
      }, { passive: true, signal: this.signal });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
        else this.start();
      }, { signal: this.signal });

      document.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest?.('[data-bh-showcase-slide]');
        if (!slide || !this.root.contains(slide)) return;
        this.show(Number(slide.dataset.slideIndex), false);
        this.stop();
      }, { signal: this.signal });
    }

    observe() {
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting && entry.intersectionRatio > .2;
        if (this.visible) this.start();
        else this.stop();
      }, { threshold: [0, .2, .6] });
      this.observer.observe(this.root);
    }

    show(nextIndex, restart = true) {
      if (this.slides.length < 1) return;
      this.index = (nextIndex + this.slides.length) % this.slides.length;

      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if ('inert' in slide) slide.inert = !active;
      });

      this.dots.forEach((dot, index) => {
        const active = index === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      if (restart) {
        this.stop();
        this.start();
      }
    }

    step(direction, restart = false) {
      this.show(this.index + direction, restart);
    }

    start() {
      if (!this.autoplay || this.reducedMotion || !this.visible || this.slides.length < 2 || this.timer) return;
      this.timer = window.setTimeout(() => {
        this.timer = 0;
        this.step(1);
        this.start();
      }, this.interval);
    }

    stop() {
      if (!this.timer) return;
      window.clearTimeout(this.timer);
      this.timer = 0;
    }

    destroy() {
      this.stop();
      this.controller.abort();
      this.observer?.disconnect();
      delete this.root.bhHeroShowcase;
    }
  }

  const mount = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      root.bhHeroShowcase?.destroy();
      root.bhHeroShowcase = new BHHeroShowcase(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  } else {
    mount();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTOR).forEach((root) => root.bhHeroShowcase?.destroy());
  });
})();
